import { memo, useEffect, useRef, useCallback } from 'react';
import CookieCategory from './CookieCategory';

const CookiePreferencesModal = memo(
  ({
    categories,
    consentSettings,
    onToggleConsent,
    onSave,
    onClose,
    isLoading,
    error
  }) => {
    const modalRef = useRef(null);
    const firstFocusableRef = useRef(null);
    const lastFocusableRef = useRef(null);

    useEffect(() => {
      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length > 0) {
        firstFocusableRef.current = focusableElements[0];
        lastFocusableRef.current =
          focusableElements[focusableElements.length - 1];
        firstFocusableRef.current.focus();
      }

      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = '';
      };
    }, []);

    const handleKeyDown = useCallback(
      (event) => {
        if (event.key === 'Escape') {
          onClose();
        }

        // Trap focus within modal
        if (event.key === 'Tab') {
          if (event.shiftKey) {
            if (document.activeElement === firstFocusableRef.current) {
              event.preventDefault();
              lastFocusableRef.current?.focus();
            }
          } else {
            if (document.activeElement === lastFocusableRef.current) {
              event.preventDefault();
              firstFocusableRef.current?.focus();
            }
          }
        }
      },
      [onClose]
    );

    const handleBackdropClick = useCallback(
      (event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      },
      [onClose]
    );

    return (
      <div
        className="cookie-preferences-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-modal-title"
        aria-describedby="preferences-modal-description"
        onKeyDown={handleKeyDown}
        onClick={handleBackdropClick}
      >
        <div className="modal-backdrop" aria-hidden="true" />

        <div ref={modalRef} className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h2 id="preferences-modal-title" className="modal-title">
                Cookie Preferences
              </h2>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close preferences modal"
                disabled={isLoading}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="modal-body">
              <p
                id="preferences-modal-description"
                className="modal-description"
              >
                Customize your cookie preferences below. You can enable or
                disable different categories of cookies to control your browsing
                experience while maintaining essential website functionality.
              </p>

              {error && (
                <div
                  className="alert alert-danger"
                  role="alert"
                  aria-live="polite"
                >
                  <strong>Error:</strong> {error}
                </div>
              )}

              <div className="cookie-categories">
                {categories.map((category) => (
                  <CookieCategory
                    key={category.id}
                    category={category}
                    isEnabled={consentSettings[category.id]}
                    onToggle={() => onToggleConsent(category.id)}
                    disabled={isLoading}
                  />
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onSave}
                disabled={isLoading}
                aria-describedby="save-button-description"
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save Preferences'
                )}
              </button>
              <div id="save-button-description" className="sr-only">
                Save your cookie preferences and close this dialog
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

CookiePreferencesModal.displayName = 'CookiePreferencesModal';

export default CookiePreferencesModal;
